// Renders AI review markdown as styled React elements — headers, bullets,
// numbered lists, tables, checkboxes, and compliance/severity badges.
//
// This is a deliberate ESTIMATING-OWNED port of src/pages/turnover/ContractMarkdown.jsx.
// Per the module-boundary rule (CLAUDE.md), the estimating "Assistants" must not
// import mutable code from the turnover module, so this small presentational
// renderer is duplicated here rather than shared. Keep the two in loose parity,
// but neither should depend on the other.
import { escapeHtml } from "../../../utils/escapeHtml";

export default function AssistantMarkdown({ text }) {
  if (!text) return null;

  const lines = text.split("\n");
  const elements = [];
  let i = 0;
  let tableBuffer = [];
  let inTable = false;

  const renderInline = (str) => {
    // Escape any raw HTML in the source FIRST (see utils/escapeHtml.js — the
    // text derives from attacker-supplied uploaded documents). The markdown
    // transforms below then inject only known-safe HTML. Do not reorder:
    // escaping must precede the tag-injecting replaces.
    let s = escapeHtml(str);
    s = s.replace(/✅\s*\*\*COMPLIANT\*\*/g, '<span class="badge-compliant">✓ Compliant</span>');
    s = s.replace(/✅\s*\*\*EXCEEDS\*\*/g, '<span class="badge-exceeds">↑ Exceeds</span>');
    s = s.replace(/❌\s*\*\*([^*]+)\*\*/g, '<span class="badge-fail">✗ $1</span>');
    s = s.replace(/⚠️\s*\*\*([^*]+)\*\*/g, '<span class="badge-warn">⚠ $1</span>');
    s = s.replace(/🔴\s*/g, '<span class="sev-high">● HIGH</span> ');
    s = s.replace(/🟡\s*/g, '<span class="sev-med">● MED</span> ');
    s = s.replace(/🟢\s*/g, '<span class="sev-low">● LOW</span> ');
    s = s.replace(/\[x\]/gi, '<span class="cb-checked">☑</span>');
    s = s.replace(/\[ \]/g, '<span class="cb-empty">☐</span>');
    s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
    s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
    return <span dangerouslySetInnerHTML={{ __html: s }} />;
  };

  const flushTable = (buf, key) => {
    if (buf.length < 2) return null;
    const headerCells = buf[0].split("|").map(c => c.trim()).filter(Boolean);
    const rows = buf.slice(2).map(row => row.split("|").map(c => c.trim()).filter(Boolean));
    return (
      <div key={key} style={{ overflowX: "auto", marginBottom: 16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--tint-blue)" }}>
              {headerCells.map((h, j) => (
                <th key={j} style={{ padding: "6px 10px", borderBottom: "2px solid #bae6fd", textAlign: "left", fontWeight: 700, color: "#0369a1", whiteSpace: "nowrap" }}>
                  {renderInline(h)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} style={{ background: ri % 2 === 0 ? "#fff" : "var(--surface2)" }}>
                {row.map((cell, ci) => (
                  <td key={ci} style={{ padding: "6px 10px", borderBottom: "1px solid #e0f2fe", verticalAlign: "top" }}>
                    {renderInline(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim().startsWith("|")) {
      if (!inTable) inTable = true;
      tableBuffer.push(line.trim());
      i++;
      continue;
    } else if (inTable) {
      elements.push(flushTable(tableBuffer, `table-${i}`));
      tableBuffer = [];
      inTable = false;
    }

    const trimmed = line.trim();

    if (trimmed.startsWith("# ")) {
      elements.push(<h1 key={i} style={{ fontSize: 16, fontWeight: 800, color: "#0c4a6e", margin: "20px 0 8px", paddingBottom: 6, borderBottom: "2px solid #bae6fd" }}>{renderInline(trimmed.slice(2))}</h1>);
    } else if (trimmed.startsWith("## ")) {
      const text2 = trimmed.slice(3);
      const isHigh = /high|critical/i.test(text2);
      const isMed  = /medium/i.test(text2);
      const color  = isHigh ? "#dc2626" : isMed ? "#d97706" : "#0369a1";
      elements.push(<h2 key={i} style={{ fontSize: 14, fontWeight: 700, color, margin: "18px 0 6px", display: "flex", alignItems: "center", gap: 6 }}>{renderInline(text2)}</h2>);
    } else if (trimmed.startsWith("### ")) {
      elements.push(<h3 key={i} style={{ fontSize: 13, fontWeight: 700, color: "#1e3a5f", margin: "14px 0 4px" }}>{renderInline(trimmed.slice(4))}</h3>);
    } else if (trimmed.startsWith("#### ")) {
      elements.push(<h4 key={i} style={{ fontSize: 12, fontWeight: 700, color: "#374151", margin: "10px 0 3px" }}>{renderInline(trimmed.slice(5))}</h4>);
    } else if (trimmed === "---" || trimmed === "***") {
      elements.push(<hr key={i} style={{ border: "none", borderTop: "1px solid #e0f2fe", margin: "16px 0" }} />);
    } else if (trimmed.match(/^[-*]\s+\[[ x]\]/i)) {
      const checked = /\[x\]/i.test(trimmed);
      const text3   = trimmed.replace(/^[-*]\s+\[[ x]\]\s*/i, "");
      elements.push(
        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "3px 0", paddingLeft: 4 }}>
          <span style={{ fontSize: 16, color: checked ? "#16a34a" : "#9ca3af", flexShrink: 0, marginTop: 1 }}>{checked ? "☑" : "☐"}</span>
          <span style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.5 }}>{renderInline(text3)}</span>
        </div>
      );
    } else if (trimmed.match(/^[-*•]\s+/)) {
      const text3  = trimmed.replace(/^[-*•]\s+/, "");
      const isFlag = /^(?:\*\*FLAG|FLAG|ACTION|Recommended Action)/i.test(text3);
      elements.push(
        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "3px 0 3px 12px" }}>
          <span style={{ color: isFlag ? "#dc2626" : "#6b7280", flexShrink: 0, marginTop: 3, fontSize: 10 }}>●</span>
          <span style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.6 }}>{renderInline(text3)}</span>
        </div>
      );
    } else if (trimmed.match(/^\d+\.\s+/)) {
      const num  = trimmed.match(/^(\d+)\./)[1];
      const text3 = trimmed.replace(/^\d+\.\s+/, "");
      elements.push(
        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "4px 0 4px 8px" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0369a1", flexShrink: 0, minWidth: 18 }}>{num}.</span>
          <span style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.6 }}>{renderInline(text3)}</span>
        </div>
      );
    } else if (trimmed.startsWith(">")) {
      elements.push(
        <blockquote key={i} style={{ margin: "8px 0", paddingLeft: 12, borderLeft: "3px solid #7dd3fc", color: "#374151", fontStyle: "italic", fontSize: 12, lineHeight: 1.6 }}>
          {renderInline(trimmed.slice(1).trim())}
        </blockquote>
      );
    } else if (!trimmed) {
      elements.push(<div key={i} style={{ height: 6 }} />);
    } else {
      elements.push(<p key={i} style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.7, margin: "4px 0" }}>{renderInline(trimmed)}</p>);
    }

    i++;
  }

  if (inTable && tableBuffer.length > 0) {
    elements.push(flushTable(tableBuffer, "table-end"));
  }

  return (
    <>
      <style>{`
        .badge-compliant { display:inline-flex; align-items:center; gap:3px; background:#d1fae5; color:#065f46; font-size:10px; font-weight:700; padding:1px 7px; border-radius:100px; }
        .badge-exceeds   { display:inline-flex; align-items:center; gap:3px; background:#dbeafe; color:#1e40af; font-size:10px; font-weight:700; padding:1px 7px; border-radius:100px; }
        .badge-fail      { display:inline-flex; align-items:center; gap:3px; background:#fee2e2; color:#991b1b; font-size:10px; font-weight:700; padding:1px 7px; border-radius:100px; }
        .badge-warn      { display:inline-flex; align-items:center; gap:3px; background:#fef3c7; color:#92400e; font-size:10px; font-weight:700; padding:1px 7px; border-radius:100px; }
        .sev-high        { color:#dc2626; font-size:10px; font-weight:800; }
        .sev-med         { color:#d97706; font-size:10px; font-weight:800; }
        .sev-low         { color:#16a34a; font-size:10px; font-weight:800; }
        .cb-checked      { color:#16a34a; font-size:14px; }
        .cb-empty        { color:#9ca3af; font-size:14px; }
        code             { background:#f1f5f9; color:#0f172a; padding:1px 5px; border-radius:4px; font-size:11px; font-family:monospace; }
      `}</style>
      {elements}
    </>
  );
}
